import AddPhotosCta from "@/components/VenueDetailBasic/AddPhotosCta";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AccessibilityCategory,
  AccessibilityTag,
  ReviewPhoto,
  VenueDetails,
} from "common/types";
import { useI18N } from "common/hooks";
import { bucketUrlPrefix } from "common/config";
import { formatFullDateAsShort } from "common/utils/general";
import { getVenueSpaceSummary, isSmartTaggedSpace } from "common/utils";
import { isVenueSociabilityVerified } from "@/components/VenueCard/VenueDataSourceBadge";
import AccessibilitySpaceCard from "@/components/common/AccessibilitySpaceCard";
import FullImageViewer from "@/components/FullImageViewer";

interface LatestReviewDetailUpdatedDate {
  latest_review_posted_at: string;
  latest_review_submitted_by: number;
  latest_review_id: number;
}

type AccessibilitySubItemProps = {
  qtagsAll: string;
  qtags: AccessibilityTag[];
  sections: any;
  reviewPhotos: ReviewPhoto[];
  fullSize?: boolean;
  venueId: string;
  venueDetails: VenueDetails;
  onFlag: (mainTag: any) => void;
  lastUpdated: string;
  categories: AccessibilityCategory[];
  categoryName: string;
  cid?: string;
  tid?: string;
  onShowDetailView?: (initialSpaceId: string) => void;
  latestReviewDetailUpdatedDate?: LatestReviewDetailUpdatedDate;
};

function AccessibilitySubItem({
  qtags,
  qtagsAll,
  venueDetails,
  reviewPhotos,
  categories,
  categoryName,
  cid,
  tid,
  onShowDetailView,
  latestReviewDetailUpdatedDate,
}: AccessibilitySubItemProps) {
  const i18n = useI18N();
  const hasAutoOpened = useRef(false);
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [photoViewerIndex, setPhotoViewerIndex] = useState(0);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  useEffect(() => setPortalRoot(document.body), []);

  // Calculate the initial space id for the detail view based on current qtagsAll
  const getInitialSpaceId = (): string => {
    for (const category of categories) {
      const tagIndex = category.tags.findIndex(
        (tag) => tag.qtagsAll === qtagsAll,
      );
      if (tagIndex !== -1) {
        return `${category.catId}-${tagIndex}`;
      }
    }
    return "";
  };

  // Auto-open detail view if cid and tid are passed
  useEffect(() => {
    if (cid && tid && onShowDetailView && !hasAutoOpened.current) {
      // Find the index from qtags where category_id = cid and tag_id = tid
      const categoryTags = categories.find(
        (category) => category.catId === parseInt(cid),
      )?.tags;
      let tagIndex = -1;
      if (categoryTags) {
        tagIndex = categoryTags.findIndex((tag) =>
          tag.qtags.find((qtag) => qtag.tagId === parseInt(tid)),
        );
      }

      // Only proceed if we found a valid tag index
      if (tagIndex === -1) {
        return;
      }

      const initialSpaceId = `${cid}-${tagIndex}`;
      hasAutoOpened.current = true;
      onShowDetailView(initialSpaceId);
    }
  }, [cid, tid, onShowDetailView, venueDetails, qtagsAll, categories]);

  const tags = qtagsAll.split(",");
  const mainTag = tags[0];
  const photos = reviewPhotos;

  // Get all tag names (excluding the main tag for display in bullet list)
  const allTagNames = qtags
    .map((qtag) => qtag.tagName)
    .filter((name) => name !== mainTag);

  const totalTagsCount = qtags.length;

  const aiSummary = getVenueSpaceSummary(
    venueDetails?.business?.venue_space_summaries,
    categoryName,
    mainTag,
  );

  // A verified venue has been checked by the team, so its tags are no longer
  // presented as machine-written — the Smart tagging framing drops away.
  const isSmartTagged =
    isSmartTaggedSpace(qtags) && !isVenueSociabilityVerified(venueDetails);

  // Same accessibility-tags panel the space detail page's lightbox shows —
  // qtags already carries every tag for this space, section id and name included.
  const photoViewerQtags = qtags.map((tag) => ({
    tagId: tag.tagId,
    tagName: tag.tagName,
    categoryDisplaySectionId: tag.categoryDisplaySectionId?.toString(),
  }));
  const photoViewerDisplaySections = qtags.reduce<Record<string, string>>(
    (sections, tag) => {
      if (tag.categoryDisplaySectionId != null) {
        sections[tag.categoryDisplaySectionId.toString()] =
          tag.categoryDisplaySectionName;
      }
      return sections;
    },
    {},
  );

  const handleViewAllClick = () => {
    if (onShowDetailView) {
      onShowDetailView(getInitialSpaceId());
    } else {
      window.location.href = `/accessibility-guide-space/${venueDetails.business.slug}/${getInitialSpaceId()}`;
    }
  };

  // Photo click opens a lightbox in place; the rest of the card (title, tags,
  // "View all") navigates to the space detail view via handleViewAllClick.
  // No photos to preview (placeholder state) → fall through to the same navigation.
  const handlePhotoClick = (index: number) => {
    if (photos.length === 0) {
      handleViewAllClick();
      return;
    }
    setPhotoViewerIndex(index);
    setIsPhotoViewerOpen(true);
  };

  if (mainTag === "No bathroom onsite") {
    return (
      <article className="bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-xl overflow-hidden">
        <div className="p-6 flex items-center justify-center">
          <h3 className="title-s-semibold text-fg-neutral-strong text-center">
            {mainTag}
          </h3>
        </div>
      </article>
    );
  }

  return (
    <>
      <AccessibilitySpaceCard
        imageMode="carousel"
        photos={photos}
        onPhotoClick={handlePhotoClick}
        title={mainTag}
        titleClassName="body-l-semibold"
        subtitle={
          latestReviewDetailUpdatedDate?.latest_review_posted_at ? (
            <p className="label-s-regular text-fg-neutral-subtle">
              {i18n.get("lastUpdated")}:{" "}
              {formatFullDateAsShort(
                latestReviewDetailUpdatedDate.latest_review_posted_at,
              )}
            </p>
          ) : undefined
        }
        aiSummary={aiSummary}
        isSmartTagged={isSmartTagged}
        tagLines={allTagNames}
        emptyTagMessage={`• ${i18n.get("noAccessibilityInformationAvailable") || "No additional accessibility information available"}`}
        emptyTagCta={<AddPhotosCta />}
        tagsCount={totalTagsCount}
        onContentClick={handleViewAllClick}
        contentAriaLabel={`${i18n.get("viewAll") || "View all"} ${totalTagsCount} ${i18n.get("accessibilityTags") || "accessibility tags"} ${i18n.get("for") || "for"} ${mainTag}`}
        viewAllLabel={i18n.get("viewAll") || "View all"}
        accessibilityTagsLabel={i18n.get("accessibilityTags") || "accessibility tags"}
      />

      {isPhotoViewerOpen &&
        photos.length > 0 &&
        portalRoot &&
        createPortal(
          <div className="fixed inset-0 z-[999] bg-black bg-opacity-90">
            <FullImageViewer
              images={photos.map((photo) => ({
                src: bucketUrlPrefix + photo.path,
                alt: photo.alt_text || mainTag,
                tag_name: mainTag,
                category_name: categoryName,
                created_at: photo.created_at,
                review_photos_id: photo.review_photos_id,
                width: photo.width,
                height: photo.height,
                sort_order: photo.sort_order,
                qtags: photoViewerQtags,
                tags: photoViewerQtags,
                displaySections: photoViewerDisplaySections,
                user_roles: photo.user_roles
                  ? { isGeneralUser: !!photo.user_roles.isGeneralUser }
                  : { isGeneralUser: false },
              }))}
              currentIndex={photoViewerIndex}
              onClose={() => setIsPhotoViewerOpen(false)}
              onFlag={() => {}}
              showFlag={false}
              showRightTab={true}
              isSmartTagged={isSmartTagged}
              pageName="Accessibility Photo"
            />
          </div>,
          portalRoot,
        )}
    </>
  );
}

export default AccessibilitySubItem;
